import withWidgetData from 'containers/user/home/components/widgets/HOC/with-widget-data-hoc';
import Announcement, {
  announcementConfig,
} from 'containers/user/home/components/widgets/announcement';
import ReleaseNotes, {
  getReleaseNotesConfig,
} from 'containers/user/home/components/widgets/release-notes';

import {
  ANNOUNCEMENT,
  RELEASE_NOTE,
  GOAL,
  VISUAL,
} from 'containers/user/home/components/widgets/widgets.constants';
import type { WidgetType } from 'containers/user/home/components/widgets/widgets.constants';

import GoalsWidget, {
  goalsConfig,
} from 'containers/user/home/components/widgets/goals-widget/goals-widget';

import PowerBIWidget, {
  powerBiWidgetConfig,
} from 'containers/user/home/components/widgets/powerbi';

import type { MasonryWidget, RowSpan } from 'containers/user/home/components/masonry-grid/masonry-grid.helper';

import { getUserWidgetContent } from 'api/generated/user-widgets/user-widgets';

import type { UseCurrentUserResult } from 'state/current-user/current-user.thunk';

const AnnouncementWithData = withWidgetData(Announcement);
const ReleaseNotesWithData = withWidgetData(ReleaseNotes);
const GoalsWidgetWithData = withWidgetData(GoalsWidget);

interface WidgetMapEntry {
  component: React.ComponentType<{
    userId: string;
    widgetId: string | number;
    hasImage?: boolean | null;
    removeWidget: (id: string | number) => void;
  }>;
  config?: { colSpan: number; rowSpan: RowSpan };
}

const widgetMap: Partial<Record<WidgetType, WidgetMapEntry>> = {
  [ANNOUNCEMENT]: {
    component: AnnouncementWithData as WidgetMapEntry['component'],
    config: announcementConfig,
  },
  [RELEASE_NOTE]: {
    component: ReleaseNotesWithData as WidgetMapEntry['component'],
  },
  [GOAL]: {
    component: GoalsWidgetWithData as WidgetMapEntry['component'],
    config: goalsConfig,
  },
  [VISUAL]: {
    component: PowerBIWidget as WidgetMapEntry['component'],
    config: powerBiWidgetConfig,
  },
};

interface WidgetItem {
  id: string | number;
  widget: { type: WidgetType };
}

interface ReleaseNotesContent {
  error?: unknown;
  data?: { attributes?: { image?: unknown } };
}

const buildGeneralWidget = (
  widgetType: WidgetType,
  item: WidgetItem,
  currentUser: UseCurrentUserResult,
  hasImage: boolean | null,
  removeWidget: (id: string | number) => void,
): MasonryWidget | null => {
  const entry = widgetMap[widgetType];

  if (!entry) {
    return null;
  }

  const { component: WidgetComponent, config } = entry;

  if (!currentUser.data) return null;

  return {
    id: item.id,
    type: widgetType,
    widgetComponent: (
      <WidgetComponent
        userId={currentUser.data.oktaUserId}
        widgetId={item.id}
        hasImage={hasImage}
        removeWidget={removeWidget}
      />
    ),
    colSpan: config?.colSpan ?? 1,
    rowSpan: config?.rowSpan ?? 1,
  };
};

const createWidgetObject = async (
  item: WidgetItem,
  currentUser: UseCurrentUserResult,
  removeWidget: (id: string | number) => void,
): Promise<MasonryWidget | null> => {
  const widgetType = item.widget.type;

  if (widgetType === RELEASE_NOTE) {
    try {
      if (!currentUser.data) return null;

      const body = (await getUserWidgetContent(
        currentUser.data.oktaUserId,
        Number(item.id),
      )) as { widget?: { content?: ReleaseNotesContent } };

      if (body.widget?.content?.error) {
        return null;
      }

      const hasImage = !!body?.widget?.content?.data?.attributes?.image;
      const dynamicConfig = getReleaseNotesConfig(hasImage);

      const generalWidget = buildGeneralWidget(
        widgetType,
        item,
        currentUser,
        hasImage,
        removeWidget,
      );

      return generalWidget ? { ...generalWidget, ...dynamicConfig } : null;
    } catch (error) {
      console.error('Error fetching release note widget data:', error);

      return null;
    }
  }

  return buildGeneralWidget(widgetType, item, currentUser, null, removeWidget);
};

const getWidgetsSet = async (
  responseData: WidgetItem[],
  currentUser: UseCurrentUserResult,
  removeWidget: (id: string | number) => void,
): Promise<MasonryWidget[]> => {
  const widgets = await Promise.all(
    responseData.map((item) =>
      createWidgetObject(item, currentUser, removeWidget),
    ),
  );

  return widgets.filter((w): w is MasonryWidget => Boolean(w));
};

export default getWidgetsSet;
